import { useEffect, useMemo, useState } from "react";
import {
  CalendarRange,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  LayoutGrid,
  List,
  Lock,
  MessageCircleReply,
  SlidersHorizontal,
  // DISABLED (for now): staff_handles + assignee filters are hidden.
  // UserCheck,
  // UserRound,
  // Users,
  X,
} from "lucide-react";
// import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { applyRangeSelection, formatRangeLabel } from "@/components/ui/date-range-picker";
import { SegmentedToggle } from "@/components/pages/calendar/facet-filter";
// import { getAvatarColorClasses } from "@/lib/messages/avatar-color";
// import { getThreadInitials } from "@/lib/messages/messages-helpers";
// import { getThreadAssignee } from "@/lib/thread-assignee";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/use-permissions";
import { isClinicHidden } from "@/lib/stores/permissions-store";
import { HealoChannelGlyph } from "@/components/messages/healo-channel-glyph";

/**
 * Compact WhatsApp brand mark — keeps the WhatsApp filter instantly recognizable. Fills with
 * currentColor (brand green by default) so callers on solid/tinted backgrounds can recolor it.
 */
function WhatsAppGlyph({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn("text-green-500", className)}
      aria-hidden
      focusable="false"
    >
      <path d="M12 2a10 10 0 0 0-8.5 15.2L2 22l4.9-1.4A10 10 0 1 0 12 2zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .1-1.6-.1-.4-.1-.9-.3-1.5-.6-2.7-1.2-4.4-3.9-4.6-4.1-.1-.2-1.1-1.4-1.1-2.7 0-1.3.7-1.9.9-2.2.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .5-.1.2-.2.3-.3.4l-.4.4c-.1.1-.3.3-.1.5.2.3.8 1.2 1.7 2 1.1 1 2 1.3 2.3 1.4.3.1.4.1.6-.1.2-.2.7-.8.9-1.1.2-.3.4-.2.6-.1.3.1 1.8.9 2.1 1 .3.2.5.2.6.4.1.2.1.8-.1 1.4z" />
    </svg>
  );
}

// Active-pill palettes, mirroring the flag/group chip styling elsewhere in the app so the
// filter bar reads as part of the same system (tinted surface, not a loud solid fill).
const TONES = {
  violet: {
    active:
      "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-500/40 dark:bg-violet-950/60 dark:text-violet-100",
    icon: "text-violet-600 dark:text-violet-300",
  },
  emerald: {
    active:
      "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/60 dark:text-emerald-100",
    icon: "text-emerald-600 dark:text-emerald-300",
  },
  amber: {
    active:
      "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-100",
    icon: "text-amber-600 dark:text-amber-300",
  },
  green: {
    active:
      "border-green-300 bg-green-100 text-green-900 dark:border-green-500/40 dark:bg-green-950/60 dark:text-green-100",
    icon: "text-green-600 dark:text-green-400",
  },
  sky: {
    active:
      "border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-500/40 dark:bg-sky-950/60 dark:text-sky-100",
    icon: "text-sky-600 dark:text-sky-300",
  },
  indigo: {
    active:
      "border-indigo-300 bg-indigo-100 text-indigo-900 dark:border-indigo-500/40 dark:bg-indigo-950/60 dark:text-indigo-100",
    icon: "text-indigo-600 dark:text-indigo-300",
  },
};

// A single toggle filter, rendered as a labelled pill. Inactive pills stay neutral but keep a
// faint tone-coloured icon as a hint; active pills adopt the full tinted treatment.
function FilterPill({ icon: Icon, glyph: Glyph, label, tone, active, onClick }) {
  const t = TONES[tone] || TONES.sky;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium leading-none shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        active
          ? cn(t.active, "shadow")
          : "border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground",
      )}
    >
      {Glyph ? (
        <Glyph className="h-3.5 w-3.5 shrink-0" />
      ) : (
        <Icon className={cn("h-3.5 w-3.5 shrink-0", active ? "" : t.icon)} strokeWidth={2.25} aria-hidden />
      )}
      {label}
    </button>
  );
}

// "Last message sent" filter: a pill matching the others that opens a range calendar. `range` is
// a { from, to } Dates object or null (no filter). Reuses the field picker's selection logic so
// the start→end→restart flow and label stay consistent across the app.
function LastMessageRangeFilter({ range, onChange }) {
  const [open, setOpen] = useState(false);
  const active = Boolean(range?.from);
  const tone = TONES.indigo;

  const handleSelect = (_nextRange, triggerDate) => {
    const { range: resolved, complete } = applyRangeSelection(range, triggerDate);
    onChange?.(resolved);
    if (complete) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Filter by last message date"
          aria-pressed={active}
          className={cn(
            "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium leading-none shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
            active
              ? cn(tone.active, "shadow")
              : "border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground",
          )}
        >
          <CalendarRange
            className={cn("h-3.5 w-3.5 shrink-0", active ? "" : tone.icon)}
            strokeWidth={2.25}
            aria-hidden
          />
          {active ? <span className="whitespace-nowrap">{formatRangeLabel(range)}</span> : "Last message"}
          <ChevronDown
            className={cn("h-3 w-3 shrink-0", active ? "opacity-70" : "text-muted-foreground/70")}
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <div className="flex items-center justify-between gap-3 border-b border-border/60 px-2.5 py-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Last message sent
          </span>
          {active ? (
            <button
              type="button"
              onClick={() => {
                onChange?.(null);
                setOpen(false);
              }}
              className="inline-flex items-center gap-1 rounded text-[11px] font-semibold text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-3 w-3" aria-hidden />
              Anytime
            </button>
          ) : null}
        </div>
        <Calendar
          mode="range"
          selected={range ?? undefined}
          onSelect={handleSelect}
          numberOfMonths={1}
          defaultMonth={range?.from}
          autoFocus={false}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The lens segmented control: All (chronological default) · Needs you (the triage piles, rose
 * count) · Pinned (local bookmarks, amber count) · Groups (WhatsApp group chats, green count).
 * A lens is a VIEW, not a filter — it renders even while the filter panel below is collapsed.
 */
function ThreadLensControl({
  lens,
  onChange,
  needsCount = 0,
  pinnedCount = 0,
  waGroupsCount = 0,
  /* , mineCount = 0 */
}) {
  const segments = [
    { id: "all", label: "All", title: "Everything, newest first — the calm default" },
    { id: "needs", label: "Needs you", title: "Bot paused + unanswered bot replies — the triage piles" },
    { id: "pinned", label: "Pinned", title: "Messages you bookmarked for later — local to you" },
    { id: "waGroups", label: "Groups", title: "WhatsApp group chats" },
    // DISABLED (for now): the "Mine" lens — thread assignment is hidden;
    // assignment lives on tasks only.
    // { id: "mine", label: "Mine", title: "Only what you've claimed" },
  ];
  return (
    <div
      role="tablist"
      aria-label="Thread list lens"
      className="flex items-center rounded-lg border border-border bg-card p-0.5 shadow-sm"
    >
      {segments.map((segment) => {
        const active = lens === segment.id;
        return (
          <button
            key={segment.id}
            type="button"
            role="tab"
            aria-selected={active}
            title={segment.title}
            onClick={() => onChange?.(segment.id)}
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active
                ? "bg-indigo-600 font-semibold text-white shadow-sm dark:bg-indigo-500"
                : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {segment.label}
            {segment.id === "needs" && needsCount > 0 ? (
              <span
                className={cn(
                  "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none",
                  active
                    ? "bg-white/25 text-white"
                    : "bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300",
                )}
              >
                {needsCount > 99 ? "99+" : needsCount}
              </span>
            ) : null}
            {segment.id === "pinned" && pinnedCount > 0 ? (
              <span
                className={cn(
                  "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none",
                  active
                    ? "bg-white/25 text-white"
                    : "bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300",
                )}
              >
                {pinnedCount > 99 ? "99+" : pinnedCount}
              </span>
            ) : null}
            {segment.id === "waGroups" && waGroupsCount > 0 ? (
              <span
                className={cn(
                  "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none",
                  active
                    ? "bg-white/25 text-white"
                    : "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300",
                )}
              >
                {waGroupsCount > 99 ? "99+" : waGroupsCount}
              </span>
            ) : null}
            {/* DISABLED (for now): the "Mine" count badge (no "mine" segment renders).
            {segment.id === "mine" && mineCount > 0 ? (
              <span
                className={cn(
                  "font-mono text-[11px] leading-none",
                  active ? "text-white/80" : "text-muted-foreground/80",
                )}
              >
                {mineCount}
              </span>
            ) : null} */}
          </button>
        );
      })}
    </div>
  );
}

/** Segmented WhatsApp-number filter — lives in the app header before search when multiple numbers exist. */
export function ThreadWhatsAppNumberControl({ whatsappNumbers = [], sectionBotId = "", onChange }) {
  // Subscribe to permission changes so a group's hidden-clinic edit re-renders this row on the
  // next /users/me refresh. Access groups can hide clinic tabs (UI-trim only; admins see all).
  usePermissions();
  const numbers = Array.isArray(whatsappNumbers) ? whatsappNumbers : [];
  const visibleNumbers = numbers.filter((n) => !isClinicHidden(n.botId));

  // If the active filter points at a now-hidden number, fall back to "All" so the user is not
  // stuck filtered by a tab they can no longer see. Boolean dep keeps the effect from re-firing
  // on every render (visibleNumbers is a fresh array each time).
  const selectedHidden = Boolean(sectionBotId) && !visibleNumbers.some((n) => n.botId === sectionBotId);
  useEffect(() => {
    if (selectedHidden) onChange?.("");
  }, [selectedHidden, onChange]);

  if (visibleNumbers.length <= 1) return null;

  const segments = [
    { id: "", label: "All", title: "Show threads from every configured WhatsApp number" },
    ...visibleNumbers.map((n) => ({
      id: n.botId,
      label: n.label,
      title: n.label,
    })),
  ];

  return (
    <div
      role="tablist"
      aria-label="Filter by WhatsApp number"
      className="flex h-8 min-w-0 shrink items-center rounded-lg border border-green-200/90 bg-green-50/50 p-0.5 shadow-sm dark:border-green-500/35 dark:bg-green-950/45"
    >
      {segments.map((segment) => {
        const active = sectionBotId === segment.id;
        const isAll = segment.id === "";
        return (
          <button
            key={segment.id || "all"}
            type="button"
            role="tab"
            aria-selected={active}
            title={segment.title}
            onClick={() => onChange?.(segment.id)}
            className={cn(
              "inline-flex h-7 min-w-0 max-w-36 shrink items-center gap-1 rounded-md px-2.5 text-xs leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500/60",
              active
                ? isAll
                  ? "bg-background font-semibold text-foreground shadow-sm ring-1 ring-green-400/70 dark:bg-green-900/85 dark:text-green-50 dark:ring-green-500/60"
                  : "bg-green-600 font-semibold text-white shadow-sm dark:bg-green-500"
                : "font-medium text-green-900/55 hover:bg-green-100/90 hover:text-green-900 dark:text-green-300/65 dark:hover:bg-green-900/55 dark:hover:text-green-100",
            )}
          >
            {active ? (
              <WhatsAppGlyph className={cn("h-3 w-3 shrink-0", !isAll && "text-white")} />
            ) : null}
            <span className="truncate">{segment.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Redesigned thread-list filter & view toolbar.
 *
 * Search, the result counter, the page-size selector, and the show/hide-filters toggle all live in
 * the shared app header (see MessagesPage). This component renders:
 *   0. The lens row — All / Needs you / Mine — always visible (a lens is a view, not a filter),
 *      with a right-aligned funnel button mirroring the header's filters toggle.
 *   1. Filter pills — locked / staff / awaiting reply / WhatsApp / mine — plus faceted
 *      "last message sent" date-range pickers (hidden while `collapsed`).
 *   2. View controls — list vs. groups segmentation and collapse/expand-all — kept visually distinct
 *      from the filters since they change presentation rather than narrowing the list.
 */
export function ThreadFilterBar({
  threads = [],
  searchQuery = "",
  lens = "all",
  onLensChange,
  needsCount = 0,
  pinnedCount = 0,
  waGroupsCount = 0,
  // DISABLED (for now): the "Mine" lens is hidden with thread assignment.
  // mineCount = 0,
  showLens = false,
  // Optional node rendered at the right of the lens row (the shown/total thread counter) —
  // next to the filter toggle, so the count sits beside what narrows it.
  resultCount = null,
  onToggleFilters,
  lockedOnly,
  onLockedOnlyChange,
  // DISABLED (for now): staff_handles + assignee filters are hidden.
  // staffHandlesOnly,
  // onStaffHandlesOnlyChange,
  awaitingReplyOnly,
  onAwaitingReplyOnlyChange,
  whatsappOnly,
  onWhatsappOnlyChange,
  appOnly,
  onAppOnlyChange,
  // assignedToMe,
  // onAssignedToMeChange,
  // assigneeId = "",
  // onAssigneeIdChange,
  whatsappNumbers = [],
  sectionBotId = "",
  onSectionBotIdChange,
  lastMessageRange = null,
  onLastMessageRangeChange,
  onClearFilters,
  threadListView = "standard",
  onThreadListViewChange,
  onCollapseAll,
  onExpandAll,
  collapsed = false,
}) {
  // DISABLED (for now): the assignee facet — thread assignment is hidden.
  // // Distinct assignees present across all threads — the options for the "filter by assignee" picker.
  // const assigneeOptions = useMemo(() => {
  //   const byId = new Map();
  //   for (const t of threads) {
  //     const a = getThreadAssignee(t);
  //     if (a && !byId.has(a.id)) byId.set(a.id, a);
  //   }
  //   return Array.from(byId.values()).sort((x, y) =>
  //     x.name.localeCompare(y.name, undefined, { sensitivity: "base" }),
  //   );
  // }, [threads]);
  // const activeAssignee = assigneeId ? assigneeOptions.find((a) => a.id === assigneeId) || null : null;
  const activeSection = sectionBotId
    ? whatsappNumbers.find((n) => n.botId === sectionBotId) || null
    : null;

  const trimmedQuery = searchQuery.trim();
  // The Groups lens IS a channel (WhatsApp group chats, a dedicated server scope) — while it's
  // active the WhatsApp/Healo-app channel pills would contradict it, so they hide instead of
  // rendering as dead toggles. The other pills (locked / awaiting / date range) still narrow
  // the scoped list server-side and stay.
  const waGroupsLensActive = lens === "waGroups";

  // DISABLED (for now): staffHandlesOnly / assignedToMe / assigneeId no longer count
  // (their pills are hidden).
  // The hidden-and-neutralized channel pills must not count either — a badge for a filter
  // the user can neither see nor untoggle on this lens would just read as hidden threads.
  const activeFilterCount =
    (lockedOnly ? 1 : 0) +
    (awaitingReplyOnly ? 1 : 0) +
    (waGroupsLensActive ? 0 : (whatsappOnly ? 1 : 0) + (appOnly ? 1 : 0)) +
    (activeSection ? 1 : 0) +
    (lastMessageRange?.from ? 1 : 0) +
    (trimmedQuery ? 1 : 0);
  const anyActive = activeFilterCount > 0;
  // Badge on the collapsed toggle: pills/pickers only (search excluded — its field stays visible
  // in the app header), so hiding the panel never hides that filters are narrowing the list.
  const badgeFilterCount = activeFilterCount - (trimmedQuery ? 1 : 0);

  const pills = [
    {
      key: "locked",
      label: "Locked",
      icon: Lock,
      tone: "violet",
      active: lockedOnly,
      onClick: () => onLockedOnlyChange?.(!lockedOnly),
    },
    // DISABLED (for now): the staff_handles filter pill — the flag is no longer applied.
    // {
    //   key: "staff",
    //   label: "Staff handles",
    //   icon: UserRound,
    //   tone: "emerald",
    //   active: staffHandlesOnly,
    //   onClick: () => onStaffHandlesOnlyChange?.(!staffHandlesOnly),
    // },
    {
      key: "awaiting",
      label: "Awaiting reply",
      icon: MessageCircleReply,
      tone: "amber",
      active: awaitingReplyOnly,
      onClick: () => onAwaitingReplyOnlyChange?.(!awaitingReplyOnly),
    },
    ...(waGroupsLensActive
      ? []
      : [
          {
            key: "whatsapp",
            label: "WhatsApp",
            glyph: WhatsAppGlyph,
            tone: "green",
            active: whatsappOnly,
            onClick: () => onWhatsappOnlyChange?.(!whatsappOnly),
          },
          {
            key: "app",
            label: "Healo app",
            glyph: HealoChannelGlyph,
            tone: "sky",
            active: appOnly,
            onClick: () => onAppOnlyChange?.(!appOnly),
          },
        ]),
    // DISABLED (for now): the "Assigned to me" pill — thread assignment is hidden;
    // assignment lives on tasks only.
    // {
    //   key: "mine",
    //   label: "Assigned to me",
    //   icon: UserCheck,
    //   tone: "sky",
    //   active: assignedToMe,
    //   onClick: () => onAssignedToMeChange?.(!assignedToMe),
    // },
  ];

  if (collapsed && !showLens) return null;

  const lensRow = showLens ? (
    <div className="flex shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
      {/* The segmented control does not fit a narrow thread column, and left to itself it
          overflowed the column entirely — the pane wrapper turned into a native scroll
          container and drew an OS scrollbar the whole width of the thread list. It scrolls
          inside its own ScrollArea instead; the count and the filter toggle stay pinned.
          `wheelToHorizontal` because a mouse (no sideways gesture) otherwise cannot reach the
          lenses that overflow a narrow column. */}
      <ScrollArea orientation="horizontal" wheelToHorizontal className="min-w-0 flex-1">
        {/* w-max: the viewport's content box is shrink-to-fit, so without it the segments
            squeeze and their labels wrap onto two lines instead of scrolling. */}
        <div className="flex w-max items-center">
          <ThreadLensControl
            lens={lens}
            onChange={onLensChange}
            needsCount={needsCount}
            pinnedCount={pinnedCount}
            waGroupsCount={waGroupsCount}
            // DISABLED (for now): the "Mine" lens is hidden with thread assignment.
            // mineCount={mineCount}
          />
        </div>
      </ScrollArea>
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {resultCount}
        {onToggleFilters ? (
          <button
            type="button"
            aria-label={collapsed ? "Show filters" : "Hide filters"}
            aria-pressed={!collapsed}
            title={collapsed ? "Show filters" : "Hide filters"}
            onClick={onToggleFilters}
            className={cn(
              "inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-md border px-1 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              collapsed
                ? "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
                : "border-indigo-300 bg-indigo-100 text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-950/60 dark:text-indigo-300",
            )}
          >
            <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
            {collapsed && badgeFilterCount > 0 ? (
              <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground">
                {badgeFilterCount}
              </span>
            ) : null}
          </button>
        ) : null}
      </div>
    </div>
  ) : null;

  if (collapsed) {
    return <TooltipProvider delayDuration={250}>{lensRow}</TooltipProvider>;
  }

  return (
    <TooltipProvider delayDuration={250}>
      {lensRow}
      <div className="shrink-0 space-y-2.5 border-b border-border bg-muted/30 px-3 py-2.5">
        {/* Filter pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          {pills.map((p) => (
            <FilterPill
              key={p.key}
              icon={p.icon}
              glyph={p.glyph}
              label={p.label}
              tone={p.tone}
              active={p.active}
              onClick={p.onClick}
            />
          ))}

          {/* DISABLED (for now): faceted assignee picker — thread assignment is hidden;
              assignment lives on tasks only.
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Filter by assignee"
                aria-pressed={Boolean(assigneeId)}
                className={cn(
                  "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium leading-none shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                  activeAssignee
                    ? cn(TONES.sky.active, "shadow")
                    : "border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground",
                )}
              >
                <Users
                  className={cn("h-3.5 w-3.5 shrink-0", activeAssignee ? "" : TONES.sky.icon)}
                  strokeWidth={2.25}
                  aria-hidden
                />
                {activeAssignee ? (
                  <span className="max-w-[7rem] truncate">{activeAssignee.name}</span>
                ) : (
                  "Assignee"
                )}
                <ChevronDown
                  className={cn("h-3 w-3 shrink-0", activeAssignee ? "opacity-70" : "text-muted-foreground/70")}
                  aria-hidden
                />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-60 p-1.5">
              <div className="px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Filter by assignee
              </div>
              <button
                type="button"
                onClick={() => onAssigneeIdChange?.("")}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left text-xs transition hover:bg-muted/70",
                  !assigneeId ? "bg-muted" : "",
                )}
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-muted-foreground/40">
                  <Users className="h-3 w-3 text-muted-foreground" aria-hidden />
                </span>
                <span className="min-w-0 flex-1 truncate">Anyone</span>
                {!assigneeId ? (
                  <span className="h-2 w-2 shrink-0 rounded-full bg-sky-500" aria-hidden />
                ) : null}
              </button>
              <div className="my-1 h-px bg-border/70" />
              <div className="max-h-64 overflow-y-auto">
                {assigneeOptions.length === 0 ? (
                  <p className="px-1.5 py-2 text-xs text-muted-foreground">No assigned threads yet.</p>
                ) : (
                  assigneeOptions.map((a) => {
                    const active = a.id === assigneeId;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => onAssigneeIdChange?.(active ? "" : a.id)}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left text-xs transition hover:bg-muted/70",
                          active ? "bg-muted" : "",
                        )}
                      >
                        <Avatar className="h-5 w-5">
                          <AvatarFallback
                            className={cn("text-[9px] font-semibold", getAvatarColorClasses(a.name))}
                          >
                            {getThreadInitials(a.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="min-w-0 flex-1 truncate">{a.name}</span>
                        {active ? (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-sky-500" aria-hidden />
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
          */}

          {/* Last-message-sent date range */}
          <LastMessageRangeFilter range={lastMessageRange} onChange={onLastMessageRangeChange} />

          {anyActive ? (
            <button
              type="button"
              onClick={onClearFilters}
              className="ml-auto inline-flex h-7 items-center gap-1 rounded-full px-2 text-[11px] font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              Clear{activeFilterCount > 1 ? ` all · ${activeFilterCount}` : ""}
            </button>
          ) : null}
        </div>

        {/* View controls — presentation, not filtering */}
        <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            View
          </span>
          <div className="inline-flex items-center gap-1.5">
            {/* The color-folders view has no meaning inside the Groups lens (folders belong to
                the default scope), and its old "Groups" label collided with the new lens — the
                visible label is now "Folders" while the id/param stays "groups" so persisted
                preferences and the server's groups= filter param are untouched. */}
            {!waGroupsLensActive ? (
              <SegmentedToggle
                ariaLabel="Thread list view"
                size="sm"
                value={threadListView}
                onChange={(v) => onThreadListViewChange?.(v)}
                segments={[
                  { id: "standard", label: "List", icon: List, title: "Flat list" },
                  { id: "groups", label: "Folders", icon: LayoutGrid, title: "Grouped by folder" },
                ]}
              />
            ) : null}
            <div
              role="group"
              aria-label="Section visibility"
              className="inline-flex items-center rounded-lg border border-border bg-background p-0.5 shadow-sm"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onCollapseAll}
                    aria-label="Collapse all sections"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ChevronsDownUp className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Collapse all
                </TooltipContent>
              </Tooltip>
              <span aria-hidden className="mx-px h-4 w-px bg-border/60" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onExpandAll}
                    aria-label="Expand all sections"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ChevronsUpDown className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Expand all
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
